import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateManualChargeRequest,
  ListChargesQuery,
  MarkPaidManualRequest,
  UpdateDueDateRequest,
} from '@control-center/shared';

import {
  acceptChargebackLoss,
  createManualCharge,
  fetchChargeDetail,
  fetchCharges,
  markPaidManual,
  retryFailedCharge,
  updateChargeDueDate,
} from '../lib/charges-api';

export const chargesKeys = {
  all: ['charges'] as const,
  lists: () => [...chargesKeys.all, 'list'] as const,
  list: (filters: ListChargesQuery) => [...chargesKeys.lists(), filters] as const,
  detail: (id: string) => [...chargesKeys.all, 'detail', id] as const,
};

export function useCharges(filters: ListChargesQuery) {
  return useQuery({
    queryKey: chargesKeys.list(filters),
    queryFn: () => fetchCharges(filters),
    staleTime: 30_000,
  });
}

export function useChargeDetail(id: string | undefined) {
  return useQuery({
    queryKey: chargesKeys.detail(id ?? ''),
    queryFn: () => fetchChargeDetail(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateManualCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateManualChargeRequest) => createManualCharge(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}

export function useMarkPaidManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { chargeId: string; payload: MarkPaidManualRequest }) =>
      markPaidManual(vars.chargeId, vars.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}

export function useUpdateChargeDueDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { chargeId: string; payload: UpdateDueDateRequest }) =>
      updateChargeDueDate(vars.chargeId, vars.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}

export function useRetryFailedCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chargeId: string) => retryFailedCharge(chargeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}

export function useAcceptChargebackLoss() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chargeId: string) => acceptChargebackLoss(chargeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}
